import { Modal } from 'react-bootstrap';
import React from 'react';

export const attendanceAlertType = {
  MISSING: 'MISSING',
  DUPLICATING: 'DUPLICATING',
  COMPLETE: 'COMPLETE',
} as const;

export type AttendanceAlertType = typeof attendanceAlertType[keyof typeof attendanceAlertType];

type MissingMessage = {
  text: string;
  classId: number;
  className: string;
  rawDate: string;
  dayOfWeek: number;
};

type AttendanceAlertModalProps = {
  show: boolean;
  onClose: () => void;
  onCreateAttendance?: (classId: number, date: string) => void;
  alerts: {
    teacherName: string;
    messages: MissingMessage[] | string[];
    attendanceAlertType: AttendanceAlertType;
  }[];
};

const isMissingMessage = (m: MissingMessage | string): m is MissingMessage =>
  typeof m === 'object' && 'classId' in m;

export const AttendanceAlertModal: React.FC<AttendanceAlertModalProps> = ({
  show,
  onClose,
  onCreateAttendance,
  alerts,
}) => {
  const missingAlerts = alerts.filter(a => a.attendanceAlertType === attendanceAlertType.MISSING);
  const otherAlerts = alerts.filter(a => a.attendanceAlertType !== attendanceAlertType.MISSING);

  const totalMissing = missingAlerts.reduce((acc, a) => acc + a.messages.length, 0);

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 60%, #42a5f5 100%)',
        borderRadius: '12px 12px 0 0',
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: 'rgba(255,255,255,0.18)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, flexShrink: 0,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>⚠️</div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem', letterSpacing: 0.3 }}>
            Nhắc nhở điểm danh
          </div>
          <div style={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.82rem', marginTop: 2 }}>
            {totalMissing > 0
              ? `Có ${totalMissing} buổi chưa được điểm danh`
              : 'Kiểm tra các buổi điểm danh bị thiếu'}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
            color: '#fff', cursor: 'pointer', fontSize: 18, padding: '4px 10px',
            transition: 'background 0.2s',
          }}
          title="Đóng"
        >✕</button>
      </div>

      {/* Body */}
      <div style={{
        padding: '20px 24px',
        maxHeight: '60vh',
        overflowY: 'auto',
        background: '#f8faff',
      }}>
        {missingAlerts.map((alert, idx) => (
          <div key={idx} style={{ marginBottom: 20 }}>
            {/* Teacher badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'linear-gradient(90deg,#1976d2,#42a5f5)',
              color: '#fff', borderRadius: 20, padding: '4px 14px',
              fontSize: '0.82rem', fontWeight: 700, marginBottom: 12,
              boxShadow: '0 2px 6px rgba(25,118,210,0.25)',
            }}>
              👤 {alert.teacherName}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {alert.messages.map((msg, i) => {
                const isMissing = isMissingMessage(msg);
                return (
                  <div key={i} style={{
                    background: '#fff',
                    border: '1.5px solid #e3eeff',
                    borderLeft: '4px solid #1976d2',
                    borderRadius: 10,
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    boxShadow: '0 1px 4px rgba(25,118,210,0.07)',
                    transition: 'box-shadow 0.18s',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                      <span style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'linear-gradient(135deg,#e3f2fd,#bbdefb)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, flexShrink: 0,
                        border: '1.5px solid #90caf9',
                      }}>📋</span>
                      <span style={{ fontSize: '0.88rem', color: '#1a237e', fontWeight: 600, wordBreak: 'break-word' }}>
                        {isMissing ? msg.text : (msg as string)}
                      </span>
                    </div>
                    {isMissing && onCreateAttendance && (
                      <button
                        onClick={() => {
                          onCreateAttendance(msg.classId, msg.rawDate);
                          onClose();
                        }}
                        style={{
                          background: 'linear-gradient(135deg,#1565c0,#1976d2)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          padding: '6px 14px',
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          boxShadow: '0 2px 6px rgba(25,118,210,0.3)',
                          transition: 'all 0.18s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-1px)')}
                        onMouseLeave={e => (e.currentTarget.style.transform = '')}
                      >
                        ✏️ Tạo Điểm Danh
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Other alerts (duplicating, etc.) */}
        {otherAlerts.length > 0 && (
          <div style={{
            background: '#fff8e1', border: '1.5px solid #ffe082',
            borderRadius: 10, padding: '10px 14px', marginBottom: 16,
          }}>
            {/* Section header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginBottom: 12,
            }}>
              <span style={{
                background: '#ff6f00', color: '#fff',
                borderRadius: 20, padding: '3px 12px',
                fontSize: '0.8rem', fontWeight: 800, letterSpacing: 0.3,
              }}>
                🔁 TRÙNG LẶP ĐIỂM DANH
              </span>
              <span style={{ fontSize: '0.8rem', color: '#bf360c', fontWeight: 600 }}>
                Các buổi điểm danh dưới đây bị ghi nhận nhiều lần
              </span>
            </div>

            {otherAlerts.map((alert, idx) => (
              <div key={`other-${idx}`} style={{ marginBottom: 12 }}>
                {/* Teacher badge */}
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#fff3e0', color: '#e65100',
                  borderRadius: 20, padding: '3px 12px',
                  fontSize: '0.8rem', fontWeight: 700, marginBottom: 8,
                  border: '1px solid #ffcc80',
                }}>
                  👤 {alert.teacherName}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {alert.messages.map((msg, i) => (
                    <div key={i} style={{
                      background: '#fff',
                      border: '1.5px solid #ffcc80',
                      borderLeft: '4px solid #ff6f00',
                      borderRadius: 8,
                      padding: '8px 12px',
                      display: 'flex', alignItems: 'center', gap: 10,
                    }}>
                      <span style={{
                        width: 26, height: 26, borderRadius: '50%',
                        background: '#fff3e0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, flexShrink: 0,
                        border: '1.5px solid #ffcc80',
                      }}>🔁</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.82rem', color: '#b71c1c', fontWeight: 700, marginBottom: 1 }}>
                          Điểm danh bị trùng lặp
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#4e342e', wordBreak: 'break-word' }}>
                          {isMissingMessage(msg) ? msg.text : (msg as string)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {alerts.length === 0 && (
          <div style={{ textAlign: 'center', color: '#90a4ae', padding: '30px 0', fontSize: '0.9rem' }}>
            Không có thông báo nào.
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '14px 24px',
        borderTop: '1px solid #e3eeff',
        display: 'flex',
        justifyContent: 'flex-end',
        background: '#fff',
        borderRadius: '0 0 12px 12px',
      }}>
        <button
          onClick={onClose}
          style={{
            background: 'linear-gradient(135deg, #1565c0, #1976d2)',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            padding: '10px 28px',
            fontSize: '0.9rem',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(25,118,210,0.35)',
            transition: 'all 0.2s',
            letterSpacing: 0.3,
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(25,118,210,0.4)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 12px rgba(25,118,210,0.35)'; }}
        >
          Đã hiểu, Đóng
        </button>
      </div>
    </Modal>
  );
};
